/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `B2BEventVirtualLineCompanies` for the Caintra Connect frontend.
 * @module pages/B2BEventVirtualLineCompanies
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/B2BEventVirtualLineCompanies.module.css";

import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import B2BEventVirtualLineSchedulesModal from "../modals/B2BEventVirtualLineSchedulesModal/index.jsx";
import {
  getB2BEvent,
  getB2BEventParticipatingCompanies,
} from "../logic/b2bEvents";
import { getUser, getUserRoleData } from "../logic/user";
import { canAccessB2BVirtualLineCompaniesHub } from "../utils/accessControl";

import { CircularProgress, Backdrop } from "@mui/material";
import { ReactComponent as ChevronLeftIcon } from "../assets/icons/BusinessEvents/ChevronLeftIcon.svg";

import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";

import { buildBuyerScheduleRowsFromParticipatingCompanies } from "../utils/b2bEventVirtualLineSchedules";

/**
 * Collaborator-only: participating companies for a B2B event with entry points
 * to the virtual line per buyer schedule slot.
 *
 * @returns {import("react").ReactElement}
 */
const B2BEventVirtualLineCompanies = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();

  const [accessOk, setAccessOk] = useState(false);
  const [eventNombre, setEventNombre] = useState("");
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRows, setModalRows] = useState([]);

  const loadData = useCallback(async () => {
    const userId = localStorage.getItem("userId");
    if (!userId || !eventId) {
      navigate("/b2bEvents", { replace: true });
      return;
    }
    setIsLoading(true);
    try {
      const [userData, permissions] = await Promise.all([
        getUser(userId, navigate),
        getUserRoleData(userId, navigate),
      ]);
      if (!canAccessB2BVirtualLineCompaniesHub(userData, permissions)) {
        navigate("/home", { replace: true });
        return;
      }
      setAccessOk(true);

      const [eventDoc, list] = await Promise.all([
        getB2BEvent(eventId, navigate),
        getB2BEventParticipatingCompanies(eventId, navigate),
      ]);
      setEventNombre(eventDoc?.nombre || "");
      setCompanies(Array.isArray(list) ? list : []);
    } finally {
      setIsLoading(false);
    }
  }, [eventId, navigate]);

  useEffect(() => {
    loadData().catch((err) => {
      logger.error("Error", { err: sanitizeError(err) });
    });
  }, [loadData]);

  const openBuyersModal = (company) => {
    const rows = buildBuyerScheduleRowsFromParticipatingCompanies([company]);
    setModalRows(rows);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalRows([]);
  };

  const handleViewVirtualLine = (row) => {
    navigate("/b2bEventUsersList", {
      state: {
        virtualLinePreset: {
          eventId: String(eventId),
          buyerUserId: row.buyerUserId,
          fecha: row.fecha,
          horaInicio: row.horaInicio,
          horaFin: row.horaFin,
          eventNombre: eventNombre || undefined,
          buyerName: row.buyerName,
          companyName: row.nombreComercial,
          returnPath: `/b2bEvent/${eventId}/virtual-line-companies`,
        },
      },
    });
    closeModal();
  };

  const companyCards = useMemo(
    () =>
      (companies || []).map((company, index) => {
        const key =
          company.idEmpresaCompradora != null
            ? String(company.idEmpresaCompradora)
            : `c-${index}`;
        return (
          <div key={key} className={styles.participantCard}>
            <div className={styles.participantDetails}>
              <span className={styles.participantName}>
                {company.nombreComercial || "—"}
              </span>
            </div>
            <div className={styles.buttonsContainer}>
              <button
                type="button"
                className={styles.viewBuyersButton}
                onClick={() => openBuyersModal(company)}
              >
                {t("pages.b2bEventVirtualLineCompanies.viewBuyers")}
              </button>
            </div>
          </div>
        );
      }),
    [companies, t],
  );

  if (!accessOk && !isLoading) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <button
              type="button"
              className={styles.returnButtonContainer}
              onClick={() => navigate("/b2bEvents")}
            >
              <ChevronLeftIcon className={styles.returnButtonIcon} />
              <span className={styles.returnButtonText}>
                {t("common.goBack")}
              </span>
            </button>
          </div>
          <div className={styles.headerInfo}>
            <h1 className={styles.title}>
              {eventNombre || t("pages.b2bEventVirtualLineCompanies.header")}
            </h1>
          </div>
        </div>
      </div>

      <div className={styles.participantsListContainer}>
        <span className={styles.participantsListTitle}>
          {t("pages.b2bEventVirtualLineCompanies.participantsSectionTitle")}
        </span>
        <div className={styles.participantsList}>
          {!isLoading && companyCards.length === 0 && (
            <div className={styles.noResults}>
              <p>{t("pages.b2bEventVirtualLineCompanies.noCompanies")}</p>
            </div>
          )}
          {companyCards}
        </div>
      </div>

      <B2BEventVirtualLineSchedulesModal
        open={modalOpen}
        onClose={closeModal}
        titleTranslationKey="pages.b2bEventVirtualLineCompanies.modalBuyersTitle"
        rows={modalRows}
        isLoading={false}
        onViewVirtualLine={handleViewVirtualLine}
      />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={isLoading}
      >
        <CircularProgress size={60} thickness={4} sx={{ color: "#4eabe7" }} />
      </Backdrop>
    </div>
  );
};

export default B2BEventVirtualLineCompanies;
